import * as React from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";

import { cn } from "./utils";
import { Pressable, View } from "react-native";
import StyledText from "../ui/styled-text";
import { useViewport } from "../../../hooks/useViewport";

const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> & {
    trackColor?: string;
    thumbColor?: string;
    tooltip?: boolean;
    marks?: number[];
    smallMarks?: number[];
    disabled?: boolean;
    containerClassName?: string;
  }
>(
  (
    {
      className,
      trackColor,
      thumbColor,
      tooltip,
      marks,
      smallMarks,
      disabled,
      containerClassName,
      ...props
    },
    ref
  ) => {
    const { isSmallScreen } = useViewport();

    return (
      <View className={cn("relative", containerClassName)}>
        <SliderPrimitive.Root
          ref={ref}
          className={cn(
            "relative flex w-full touch-none select-none items-center cursor-pointer",
            className
          )}
          {...props}
          disabled={disabled}
        >
          <SliderPrimitive.Track
            className={cn(
              "relative h-1.5 w-full grow overflow-hidden rounded-full"
            )}
            style={{
              backgroundColor: !!trackColor ? trackColor : "bg-gray-200",
            }}
          >
            <SliderPrimitive.Range className="absolute h-full bg-gray-800" />
          </SliderPrimitive.Track>
          {tooltip && (
            <View className="absolute -top-6 left-1/2 -translate-x-1/2 items-center flex-row">
              <StyledText className="text-sm text-center">
                {props.value}
              </StyledText>
            </View>
          )}
          <SliderPrimitive.Thumb
            className={cn(
              "z-50 block h-4 w-4 rounded-full border border-primary/50 shadow transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
            )}
            style={{
              backgroundColor: !!thumbColor ? thumbColor : "bg-background",
            }}
          />
        </SliderPrimitive.Root>
        {/* Add tick marks */}
        {marks && marks.length > 0 && (
          <View className="relative w-full flex-row justify-between mt-0 -z-10">
            {marks.map((mark) => (
              <Pressable
                onPress={() => props.onValueChange?.([mark])}
                key={mark}
                className="px-2"
                disabled={disabled}
                style={{
                  left: `${
                    ((mark - props.min!) / (props.max! - props.min!)) *
                    (isSmallScreen ? 94 : 96)
                  }%`,
                  position: "absolute",
                  transform: [{ translateX: 0 }],
                }}
              >
                <View
                  className={`w-0.5 ${
                    smallMarks?.includes(mark) ? "h-[8px]" : "h-[12px]"
                  } bg-[#f3f4f6]`}
                ></View>
              </Pressable>
            ))}
          </View>
        )}
      </View>
    );
  }
);
Slider.displayName = SliderPrimitive.Root.displayName;

export { Slider };
